/* THE PHOTO REF GRAMMAR — one definition, imported by both runtimes.
 *
 * PHASE 4, STEP 3. This file exists to close a carry-forward that was
 * logged the day it was created:
 *
 *   `functions/_lib/photo-delivery.js` parses `*_ref` values on the
 *   edge. `src/mapper.js` writes them in the browser. The two runtimes
 *   share no module — `src/` is bundled by Vite, `functions/` runs on
 *   Workers — so the grammar was stated TWICE.
 *
 * ── ⚠ AND THEY HAD ALREADY DRIFTED, WHICH WAS NOT THEORETICAL ──────
 * Measured before this file was written, not predicted:
 *
 *   given `r2:1122…eeff`
 *     photo-delivery.js  → { scheme: 'r2', hash: '1122…eeff' }   a photo
 *     mapper.js          → null                                  NO photo
 *
 * The mapper's parser required exactly three colon-separated parts and
 * a valid slot, because `idb:<holdingId>:<slot>` was all anything
 * produced. An `r2:` ref has two. So the browser would have read every
 * migrated photo as an empty slot — silently, since "null" there means
 * "nothing to show" rather than "something is wrong".
 *
 * It never fired because the mapper's parser has no callers yet. **A
 * second definition that disagrees and is not yet reachable is the
 * cheapest possible version of this bug, and it is the only version
 * anyone gets to fix in advance.**
 *
 * ── WHY THE REF CARRIES ONLY THE HASH ──────────────────────────────
 * Jesse chose the content-addressed key, 2026-08-12. A ref is
 * `r2:<contentHash>` and NOTHING ELSE. Every other segment of the
 * object key comes from somewhere the caller does not control:
 *
 *   photos/{ownerId}/{holdingId}/{slot}/{tier}-{hash8}.jpg
 *           ↑ the row   ↑ the validated path   ↑ the request
 *
 * so a ref is not a location and cannot be used as one. That is what
 * keeps the object key a boundary rather than a caller-supplied string
 * — the Phase 2 Step 4 rule, that no endpoint mints a URL for a key
 * somebody handed it.
 *
 * ── ⚠⚠ THE TRAP THIS DESIGN SETS, AND JESSE NAMED IT IN THE SAME
 *        BREATH AS APPROVING IT ────────────────────────────────────
 *   *"the photo keys have to move with the refs. Renaming without
 *    moving the bytes orphans every photo — that's the trap the
 *    legacy-id migration was cut to avoid."*
 *
 * **THE OBJECT KEY CONTAINS THE HOLDING ID.** So rewriting a holding's
 * id — which the deferred legacy-id import must do, since `"h" +
 * Date.now()` is not a uuid — changes where its photos live. Rewrite
 * the id without COPYING the objects to the new key and the row
 * survives, the ref survives, and the photograph is gone. Nothing
 * reports it: the ref still parses, the key still builds, and R2
 * answers "no such object" for a key that was correct an hour ago.
 *
 * `ref-grammar-check` asserts that changing the holding id changes the
 * key, so this coupling cannot be quietly assumed away later.
 *
 * ── ⚠ AND ON DELETION, DECIDED 2026-08-12 ──────────────────────────
 * Jesse: deleting a note does NOT delete its objects. *"An orphaned
 * object costs a fraction of a cent and the month-6 purge sweeps the
 * prefix anyway. Deleting bytes another note still points at is
 * unrecoverable, and that asymmetry decides it."* Reference counting
 * or a sweep is a Phase 7 lifecycle decision, taken deliberately —
 * never as a side effect of a delete aimed at something else, which is
 * the same reasoning that put brand images on their own prefix.
 */

/** A content hash as it appears in a ref: hex, 8 to 64 characters. */
export const HASH_PATTERN = /^[0-9a-f]{8,64}$/

/** The slots an `idb:` ref can name. Brand images never had one —
 *  they lived in local storage keyed by username, never in a column. */
export const IDB_SLOTS = Object.freeze(['front', 'back'])

/** The slots that belong to a PROFILE, not to a note. Named here only
 *  so a writer can refuse one with a message that says what went
 *  wrong — see `idbRef`. */
export const BRAND_SLOTS = Object.freeze(['logo', 'banner'])

/**
 * ⚠ PARSE IT BEFORE IT IS STORED. Jesse, 2026-08-12: *"validate at the
 * boundary rather than trust and discover later."*
 *
 * Every writer below runs its own output back through the parser and
 * throws unless it reads back as the thing it meant to write. It looks
 * redundant — the writers validate their inputs already — and it is
 * not, for one reason: **the two halves can drift.** A parser tightened
 * without its writer, or a writer extended without its parser, produces
 * refs that store cleanly and are unreadable afterwards, in a column
 * that outlives the process that wrote it. The failure surfaces later,
 * to a reader, as a photograph that will not load.
 *
 * This makes that impossible to ship: a writer that cannot round-trip
 * its own output fails HERE, at the moment of writing, where the stack
 * trace names the caller.
 */
function sealed(ref, expectedScheme) {
  const back = parsePhotoRef(ref)
  if (back.scheme !== expectedScheme) {
    throw new Error(
      `photo-ref: refusing to store ${JSON.stringify(ref)} — it writes as ${expectedScheme} but parses as ${back.scheme}. The writer and the parser have drifted.`
    )
  }
  return ref
}

/** `r2:<contentHash>` — a photo whose bytes are in the bucket.
 *
 *  Throws on a bad hash rather than returning something. A ref is
 *  written into a column that outlives this process, and a malformed
 *  one is only ever discovered later, by a reader, as a broken photo. */
export function r2Ref(contentHash) {
  if (!HASH_PATTERN.test(contentHash ?? '')) {
    throw new Error('r2Ref: contentHash must be 8-64 hex characters')
  }
  return sealed(`r2:${contentHash}`, 'r2')
}

/** `idb:<holdingId>:<slot>` — Phase 3's ref, pointing into the owner's
 *  own browser. Still written until the upload path lands; still
 *  READ long after, because a collector who has not opened the app
 *  since the migration still has rows saying this. */
export function idbRef(holdingId, slot) {
  if (typeof holdingId !== 'string' || holdingId === '' || holdingId.includes(':')) {
    throw new Error('idbRef: holdingId must be a non-empty string containing no colon')
  }
  /* ⚠ A BRAND SLOT HERE IS REFUSED BY NAME, not by falling through the
   * generic slot check. `logo` and `banner` are real slots on the photo
   * ENDPOINT, so a caller reaching for one is making a category error
   * rather than a typo — brand images live in `logo_ref`/`banner_ref`
   * on a profile and were never in IndexedDB at all. An error saying
   * "must be front or back" would send them looking for a spelling
   * mistake. */
  if (BRAND_SLOTS.includes(slot)) {
    throw new Error(
      `idbRef: "${slot}" is a PROFILE slot, not a note slot. Brand images are stored on the profile (logo_ref / banner_ref) and never had an idb: ref.`
    )
  }
  if (!IDB_SLOTS.includes(slot)) throw new Error(`idbRef: slot must be one of ${IDB_SLOTS.join(', ')}`)
  return sealed(`idb:${holdingId}:${slot}`, 'idb')
}

/**
 * Parse a `*_ref` column value. Always returns an object, never null.
 *
 *   { scheme: null }                     no photo — the ordinary case
 *   { scheme: 'r2',  hash }              in the bucket
 *   { scheme: 'idb', holdingId, slot }   still in the owner's browser
 *   { scheme: 'malformed' }              claims a photo, cannot be read
 *
 * ── ⚠ THE FOUR-WAY SPLIT IS THE WHOLE POINT, AND TWO PAIRS OF IT ARE
 *      EASY TO COLLAPSE ──────────────────────────────────────────────
 *
 *   null vs malformed — "this slot is empty" and "this slot says it
 *   has a photo and the ref is unreadable" must not be the same
 *   answer. Collapsing them renders a BROKEN photo as an EMPTY one,
 *   silently and permanently, which is the failure a collector would
 *   never report because it looks like they simply never added it.
 *
 *   idb vs r2 — the difference between "not uploaded yet" and "gone".
 *   Every real photo in this project is `idb:` as this is written.
 */
export function parsePhotoRef(ref) {
  if (typeof ref !== 'string' || ref === '') return { scheme: null }

  if (ref.startsWith('r2:')) {
    const hash = ref.slice(3)
    return HASH_PATTERN.test(hash) ? { scheme: 'r2', hash } : { scheme: 'malformed' }
  }

  if (ref.startsWith('idb:')) {
    const parts = ref.split(':')
    if (parts.length !== 3) return { scheme: 'malformed' }
    const [, holdingId, slot] = parts
    if (!holdingId || !IDB_SLOTS.includes(slot)) return { scheme: 'malformed' }
    return { scheme: 'idb', holdingId, slot }
  }

  /* An unknown scheme is malformed, NOT empty. A future `s3:` or a
   * truncated value is a fault to surface, not a slot to render blank. */
  return { scheme: 'malformed' }
}
